/**
 * Estado de um pagamento aplicado e o saldo derivado da venda (T004).
 *
 * Domínio puro: aritmética exclusivamente em `Centavos` inteiros
 * (`src/client/domain/precificacao/dinheiro.ts`, Constitution V) — nenhum
 * `double` participa de saldo, troco ou valor aplicado.
 */

import { centavos, somar, subtrair, ZERO_CENTAVOS, type Centavos } from '../precificacao/dinheiro';
import { geraTroco, MEIO_PAGTO, type FormaPagamento, type MeioPagtoNFe } from './formaPagamento';
import { ehFormaDeValeDevolucao } from './valeDevolucao';
import type { IntegracaoPagamento } from './roteamentoIntegracao';

/**
 * Máquina de estados de `PagamentoAplicado` — `data-model.md` §4.
 *
 * `EXCLUIDO` foi acrescentado ao contrato original (pedido do usuário,
 * 2026-09-04): a exclusão de uma forma pelo operador deixou de apagar o
 * registro do array e passou a ser mais um estado terminal, ao lado de
 * `RECUSADO` — a diferença é só a causa (gesto do operador, não veredito de
 * integração). `calcularSaldo` e `montarPagamentosParaPayload` já filtram por
 * `'APROVADO'`, então uma forma `EXCLUIDA` sai do saldo e do envelope ao ERP
 * sem precisar de um filtro próprio nos dois lugares.
 */
export type StatusPagamento = 'PENDENTE_INTEGRACAO' | 'APROVADO' | 'RECUSADO' | 'EXCLUIDO';

/**
 * O que a feature 010 observou ao ver o TEF aprovado (`CNC`), congelado no
 * instante da aprovação — nada o relê depois (`data-model.md` §1 da 010).
 *
 * **Forma reescrita em AD-259**, quando o SDT `CheckoutFaturarNFCe` da KB
 * trocou `TEFidentificacao`/`TEFCNPJ`/`TEFNumeroAutorizacao` por `TEFPagId`: o
 * ERP passou a ler autorização, CNPJ do adquirente e bandeira da própria
 * `TransacaoTEF`. Os três campos antigos (`identificacao`, `cnpj`,
 * `numeroAutorizacao`) saíram daqui junto.
 *
 * Opaco para o domínio de pagamento geral: só `formaParaRetrato` e a janela do
 * TEF leem os campos.
 */
export interface DadosTEF {
  /**
   * `payment_identifier` da SmartTEF — vira `TEFPagId` no retrato e é a chave
   * do estorno. É o único campo que o ERP precisa para faturar.
   */
  readonly pagId: string;
  /** `card_brand` observado; vira `TEFBandeira`. `''` quando ausente. */
  readonly bandeira: string;
  /** `nsu_host` — só tela (linha "NSU"). */
  readonly nsu: string;
  /** `autorization_code`, grafia da SmartTEF — só tela (linha "Autorização"). */
  readonly autorizacao: string;
  /** `TEFTipoIntegracao` (`NFCe_tpIntegra`): `'1'` é pagamento integrado. */
  readonly tipoIntegracao: '1';
}

/**
 * Pagamento aplicado à venda. `meioPagtoNFe`/`integracaoCartao` são cópias
 * congeladas do catálogo no momento da aplicação — o pagamento nunca resolve
 * seus dados olhando o catálogo depois (`data-model.md` §2, "Regra de
 * fronteira").
 */
export interface PagamentoAplicado {
  readonly idPagamento: string;
  readonly formaCodigo: number;
  readonly meioPagtoNFe: MeioPagtoNFe;
  readonly integracaoCartao: '1' | '2' | '';
  /** Ecoado no payload (`FR-022`/AD-111). */
  readonly entrada: string;
  /**
   * `FpgUtiCar` do catálogo, congelado como `entrada` e pelo mesmo motivo.
   *
   * É por ele que `PCheckout_ValidarNFCe` reconhece **crediário** ao somar
   * `&TotalCrediario` (`specs/014-validacao-previa-nfce/contracts/erp-validacao-api.md`).
   * Passou a ser guardado ao implementar a 014: sem ele, uma segunda inserção
   * não conseguiria projetar as formas já aplicadas com o campo preenchido, o
   * total de crediário chegaria zerado ao ERP e o gate aprovaria a venda que
   * existe para barrar — mesmo modo de falha de `entrada` (AD-111).
   *
   * Resolver pelo catálogo na hora de montar o payload não serviria: um
   * bootstrap revalidado no meio da venda reclassificaria um pagamento já
   * aprovado (`data-model.md` §2, "Regra de fronteira").
   */
  readonly fpgUtiCar: string;
  readonly valorAplicado: Centavos;
  readonly valorRecebido: Centavos | null;
  readonly integracao: IntegracaoPagamento;
  readonly status: StatusPagamento;
  readonly dadosTEF: DadosTEF | null;
  /**
   * Identificador da cobrança PIX. Guardado já na **geração** (feature 018, US4),
   * e não só na aprovação: a cobrança desistida precisa dele no `Log` do ERP.
   * Só a forma `APROVADO` vai ao payload, então o valor guardado numa forma
   * pendente nunca chega a `FormaPixGUID`.
   */
  readonly pixGuid: string | null;
  /**
   * `payment_identifier` da cobrança TEF **criada**, antes da aprovação (feature
   * 018, US4). Existe ao lado de `dadosTEF.pagId`, e não no lugar dele, porque
   * `dadosTEF` descreve o que a SmartTEF aprovou (bandeira, NSU, autorização) e
   * só existe depois do `CNC`. `null` sem cobrança criada e fora do TEF.
   */
  readonly tefPagId: string | null;
  readonly ticketDevolucao: string | null;
  /**
   * A forma entrou **com o documento** (DAV importado ou rascunho de NFCe
   * retomado), não por um gesto do operador nesta venda.
   *
   * Não muda nada no cálculo do saldo nem no payload — é a mesma forma
   * aprovada, com o mesmo valor. Muda o que o Checkout **diz** ao operador em
   * dois pontos, e é por isso que o campo existe em vez de ser inferido:
   *
   * 1. a grid trava assim que a retomada termina, e a causa não é um gesto
   *    dele — o aviso precisa nomear o documento, senão a frase acusa o
   *    operador de algo que ele não fez;
   * 2. "Limpar" sobre esta forma joga fora o registro de um valor que **já foi
   *    recebido** e está gravado no documento dentro do ERP, o que é bem mais
   *    grave do que descartar uma cobrança que ele acabou de digitar.
   *
   * Inferir de `identidadeVenda.origem` não serviria: a venda importada de um
   * DAV pendente de cobrança recebe formas do operador depois, e elas ficariam
   * marcadas como se tivessem vindo do documento.
   */
  readonly veioDeDocumento: boolean;
}

/** Derivado, nunca armazenado — seletor puro sobre carrinho + pagamentos. */
export interface SaldoPagamento {
  readonly totalLiquido: Centavos;
  /**
   * Σ `valorAplicado` dos aprovados — o que abate a venda. Não é o que vai ao ERP
   * em `FormaValor`: o dinheiro vai com o recebido (`totalRecebido`), e o ERP
   * deriva o troco da diferença (feature 018).
   */
  readonly totalAplicado: Centavos;
  /**
   * Σ do que o operador **entregou**: `valorRecebido` quando existe (dinheiro),
   * `valorAplicado` nas demais formas (correção do usuário, 2026-09-10).
   *
   * Difere de `totalAplicado` exatamente pelo troco. A tela rotula esta métrica
   * como "Recebido", e antes ela mostrava `totalAplicado` — que é limitado ao
   * saldo por `derivarValores`: quem entregava R$ 100 numa venda de R$ 50 lia
   * "Recebido R$ 50,00", o valor da venda em vez do valor da cédula. Os dois
   * números precisam existir separados porque servem a perguntas diferentes:
   * `totalAplicado` fecha a nota, `totalRecebido` fecha a gaveta.
   */
  readonly totalRecebido: Centavos;
  readonly saldoRestante: Centavos;
  readonly troco: Centavos;
}

export type ResultadoValidacao =
  | { readonly ok: true }
  | {
      readonly ok: false;
      readonly motivo: 'DINHEIRO_DUPLICADO' | 'SALDO_JA_COBERTO' | 'VALOR_ACIMA_DO_SALDO';
    };

/**
 * Algoritmo de `data-model.md` §6: `totalLiquido` é o subtotal do carrinho
 * menos o desconto de capa; `totalAplicado` soma **apenas** pagamentos
 * `APROVADO` (`PENDENTE_INTEGRACAO` não conta — `FR-004`/`FR-005`);
 * `saldoRestante` nunca fica negativo; `troco` só existe quando há um
 * pagamento em dinheiro aprovado, e é sempre `Σ recebido − Σ aplicado` **desse**
 * pagamento (no máximo um, por I2) — nenhuma outra forma gera troco (`FR-012`).
 */
export function calcularSaldo(
  subtotalCarrinho: Centavos,
  descontoCapa: Centavos,
  pagamentos: readonly PagamentoAplicado[],
): SaldoPagamento {
  // Nunca negativo. I8 já garante `desconto <= subtotal` no momento em que o
  // desconto é aplicado, mas o subtotal cai depois — ao esvaziar o carrinho no
  // fim da venda, por exemplo — e um desconto sobrevivente produziria um "Total
  // a pagar" negativo em tela. Zero é a leitura correta: não há o que cobrar.
  const totalLiquido = centavos(Math.max(0, subtrair(subtotalCarrinho, descontoCapa)));
  const aprovados = pagamentos.filter((pagamento) => pagamento.status === 'APROVADO');
  const totalAplicado = aprovados.reduce<Centavos>(
    (acumulado, pagamento) => somar(acumulado, pagamento.valorAplicado),
    ZERO_CENTAVOS,
  );
  const saldoRestante = centavos(Math.max(0, totalLiquido - totalAplicado));

  // `valorRecebido ?? valorAplicado`: só dinheiro carrega `valorRecebido` (I3),
  // e nas demais formas o que entrou é exatamente o que foi aplicado.
  const totalRecebido = aprovados.reduce<Centavos>(
    (acumulado, pagamento) => somar(acumulado, pagamento.valorRecebido ?? pagamento.valorAplicado),
    ZERO_CENTAVOS,
  );

  const dinheiro = aprovados.find((pagamento) => pagamento.meioPagtoNFe === MEIO_PAGTO.Dinheiro);
  const troco =
    dinheiro !== undefined && dinheiro.valorRecebido !== null
      ? centavos(Math.max(0, dinheiro.valorRecebido - dinheiro.valorAplicado))
      : ZERO_CENTAVOS;

  return { totalLiquido, totalAplicado, totalRecebido, saldoRestante, troco };
}

/**
 * O pagamento ainda participa da venda? `RECUSADO` e `EXCLUIDO` ficam de fora:
 * os dois já saíram do fluxo — a forma riscada permanece no array só por
 * rastreabilidade (AD-163), e contá-la travava a venda sem pagamento nenhum
 * (AD-171).
 *
 * Um predicado só para todo "há pagamento aqui?" da base (revisão de
 * 2026-10-16): a regra vivia copiada em meia dúzia de lugares, e um status
 * terminal novo acrescentado a uma cópia e não às outras faria a faixa de
 * atalhos, a troca de condição e o desconto de capa discordarem sobre a mesma
 * venda.
 */
export function pagamentoVivo(pagamento: PagamentoAplicado): boolean {
  return pagamento.status !== 'RECUSADO' && pagamento.status !== 'EXCLUIDO';
}

/** A venda tem algum pagamento vivo (`pagamentoVivo`)? */
export function haPagamentoVivo(pagamentos: readonly PagamentoAplicado[]): boolean {
  return pagamentos.some(pagamentoVivo);
}

/**
 * `FR-013`/AD-036 (I2): recusa uma segunda forma `Dinheiro`, considerando
 * apenas pagamentos que não estejam `RECUSADO` — um pagamento recusado já foi
 * retirado do fluxo e não deveria travar uma nova tentativa.
 *
 * `VALOR_ACIMA_DO_SALDO` (`FR-024`): uma forma que **não gera troco** — tudo o
 * que não é `Dinheiro` — não pode receber mais do que o saldo em aberto. O
 * excedente não teria para onde ir: `FR-012` proíbe troco fora do dinheiro, de
 * modo que a única saída seria truncar o valor em silêncio e registrar no ERP
 * um `FormaValor` diferente do que o operador digitou — justamente o que se
 * pede que não aconteça. Trinta reais cobrados a mais no cartão não voltam pelo
 * caixa: quem estorna é a operadora. Recusar com aviso devolve a decisão a quem
 * pode tomá-la.
 *
 * Os dois últimos parâmetros são **opcionais** por design: esta função decide
 * `DINHEIRO_DUPLICADO` sem depender de nenhum deles. `SALDO_JA_COBERTO` exige o
 * saldo já calculado (via `calcularSaldo`) e `VALOR_ACIMA_DO_SALDO` exige
 * também o valor que o operador digitou. Passá-los é o que permite ao slice
 * reaproveitar a mesma checagem para os três casos sem duplicar regra; omiti-los
 * mantém a função utilizável em qualquer teste que só queira a duplicidade.
 */
export function podeAplicarForma(
  forma: FormaPagamento,
  pagamentosAtuais: readonly PagamentoAplicado[],
  saldoRestante?: Centavos,
  valorInformado?: Centavos,
): ResultadoValidacao {
  // `EXCLUIDO` entra na mesma exclusão de `RECUSADO`: uma forma que o operador
  // riscou da lista não deveria travar uma nova tentativa de dinheiro, pelo
  // mesmo motivo — ela já saiu do fluxo (pedido do usuário, 2026-09-04).
  const naoRecusados = pagamentosAtuais.filter(pagamentoVivo);

  if (
    forma.meioPagtoNFe === MEIO_PAGTO.Dinheiro &&
    naoRecusados.some((pagamento) => pagamento.meioPagtoNFe === MEIO_PAGTO.Dinheiro)
  ) {
    return { ok: false, motivo: 'DINHEIRO_DUPLICADO' };
  }

  if (saldoRestante === undefined) {
    return { ok: true };
  }

  if (saldoRestante === ZERO_CENTAVOS) {
    return { ok: false, motivo: 'SALDO_JA_COBERTO' };
  }

  // O vale devolução é a exceção: o valor não foi digitado, é o do ticket, e o
  // ERP baixa `DevValTot` inteiro na hora de faturar — recusar aqui deixaria o
  // operador sem nenhuma saída para um vale maior que a compra. Ele **pode**
  // entrar, com a diferença perdida, desde que o operador confirme isso
  // explicitamente; a confirmação é responsabilidade de `aplicarValeDevolucao`,
  // não desta função pura (`FR-024`/`FR-026`).
  if (
    valorInformado !== undefined &&
    !geraTroco(forma) &&
    !ehFormaDeValeDevolucao(forma) &&
    valorInformado > saldoRestante
  ) {
    return { ok: false, motivo: 'VALOR_ACIMA_DO_SALDO' };
  }

  return { ok: true };
}

/**
 * Única forma de obter o par `valorAplicado`/`valorRecebido` — é o que torna
 * I3/I5 impossíveis de violar no call site (`data-model.md` §6).
 *
 * Para `Dinheiro`: `valorAplicado = min(valorInformado, saldoRestante)` e
 * `valorRecebido = valorInformado` — o excedente nunca entra em
 * `valorAplicado`, ele só aparece como troco em `calcularSaldo`.
 *
 * Para qualquer outra forma: `valorRecebido = null` e `valorAplicado` continua
 * limitado ao saldo. Esse `min` deixou de ser o caminho normal desde `FR-024`:
 * um valor acima do saldo numa forma sem troco é **recusado** por
 * `podeAplicarForma` antes de chegar aqui, em vez de truncado em silêncio. Ele
 * permanece como rede de segurança — se um chamador futuro esquecer de passar
 * `valorInformado` à validação, o pior desfecho é um valor truncado, nunca um
 * `FormaValor` acima do saldo numa forma que não gera troco. (No dinheiro o
 * `FormaValor` passa do total de propósito: vai o recebido, e o ERP gera o
 * troco — feature 018.)
 */
export function derivarValores(
  forma: FormaPagamento,
  valorInformado: Centavos,
  saldoRestante: Centavos,
): { readonly valorAplicado: Centavos; readonly valorRecebido: Centavos | null } {
  const valorAplicado = centavos(Math.min(valorInformado, saldoRestante));

  if (forma.meioPagtoNFe === MEIO_PAGTO.Dinheiro) {
    return { valorAplicado, valorRecebido: valorInformado };
  }
  return { valorAplicado, valorRecebido: null };
}
